import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {PlayersStatsApi} from "../../services/api/PlayersStatsApi";
import {UserWithStats} from "../../models/UserWithStats";
import {UiLoader} from "../ui-components/loader/uiLoader";
import {Top3PlayerCard} from "./Top3PlayerCard";
import {LeaderboardItem} from "./LeaderboardItem";
import {PlatformType, SelectGameMode} from "../selectGameMode/SelectGameMode";
import {AppContext} from "../authProvider/AuthProvider";
import {LeaderboardStrategy} from "../../models/leaderboard/leaderboardStrategy";
import {getLeaderboardStrategy} from "../../models/leaderboard/leaderboardStrategyFactory";
import {GameApi} from "../../services/api/GameApi";


export const Leaderboard: React.FC = () => {
    const {t} = useTranslation();
    const {errorToast} = React.useContext(AppContext);
    
    const [isFetchingData, setIsFetchingData] = React.useState(false);
    const [leaderboardData, setLeaderboardData] = React.useState<UserWithStats[]>([]);
    
    const [platform, setPlatform] = React.useState<PlatformType>(PlatformType.web);
    const [fastailyGameModeId, setFastailyGameModeId] = React.useState<number | null>(null);
    const [leaderboardStrategy, setLeaderboardStrategy] = React.useState<LeaderboardStrategy>(new LeaderboardStrategy());

    useEffect(() => {
        GameApi.getAllGameModes()
            .then((gamemodes) => {
                const fastailyGameMode = gamemodes
                    .find(gm => gm.name === "Fastaily" && gm.idGameTheme != null)?.id;

                const fastailyDifficultyId = gamemodes.find(gm => gm.idGameTheme == fastailyGameMode)
                setFastailyGameModeId(fastailyDifficultyId?.id ?? null)
            })
    }, []);

    async function fetchData(platform: PlatformType, gameModeId: number) {
        setIsFetchingData(true);
        setPlatform(platform);

        PlayersStatsApi
            .getLeaderboard(platform, gameModeId)
            .then((data) => {
                setLeaderboardStrategy(getLeaderboardStrategy(fastailyGameModeId == gameModeId))

                if (!Array.isArray(data) || data === null) {
                    errorToast();
                } else {
                    setLeaderboardData(data);
                }
                setIsFetchingData(false);
            })
            .catch(() => errorToast());
    }
    
    return (
        <div className="px-12 md:px-28 xl:px-44 pt-20 pb-12 md:pt-28 xl:pt-30 gap-8 flex flex-col h-full overflow-y-auto">
            <h1 className="text-4xl">
                {t("global.leaderboard")} - {platform === PlatformType.web ? t("global.web") : t("global.mobile")}
            </h1>

            <div className="flex flex-col w-full items-end gap-4">
                <SelectGameMode
                    onChange={fetchData}
                    onApiError={errorToast}
                />

                <p className="text-2xl">{leaderboardStrategy.getGamemodeSubtitle(t)}</p>
            </div>

            {isFetchingData ? (
                <div className="w-full h-full flex justify-center items-center">
                    <UiLoader
                        isOpen={isFetchingData}
                        text={t("global.loading-data")}
                    />
                </div>
           ) : leaderboardData.length !== 0 ? (
                <div className="flex flex-col gap-y-10">
                    <div className="flex flex-col justify-center lg:flex-row gap-12">
                        {leaderboardData[0] !== undefined && <Top3PlayerCard
                            className="lg:order-2"
                            user={leaderboardData[0]}
                            position={1}
                        />}

                        {leaderboardData[1] !== undefined && <Top3PlayerCard
                            className="lg:order-1 lg:mt-12"
                            user={leaderboardData[1]}
                            position={2}
                        />}

                        {leaderboardData[2] !== undefined && <Top3PlayerCard
                            className="lg:order-3 lg:mt-20"
                            user={leaderboardData[2]}
                            position={3}
                        />}
                    </div>

                    <div className="flex flex-col w-full items-center gap-2">
                        {leaderboardData.slice(3).map((user) => (
                            <LeaderboardItem
                                key={user.id}
                                user={user}
                                leaderboardStrategy={leaderboardStrategy}
                            />
                        ))}
                    </div>
                </div>
            ) : (
                <p className="flex text-xl w-full h-full justify-center items-center">
                    {t("leaderboard.nobody-is-ranked")}
                </p>
            )}
        </div>
    )
}
